import { Layout, Menu, MenuProps } from 'antd';
import { FC, useCallback, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isProduction } from 'src/constants';
import { getSiteMap } from 'src/routes';
import { useAuth } from 'src/stores/auth-context';

import logoWhite from 'src/assets/icon/ic-gooduncles-white.svg';

import classes from './MainNavigation.module.scss';

const { Sider } = Layout;

/** xs: '480px', sm: '576px', md: '768px', lg: '992px', xl: '1200px', xxl: '1600px', */
const breakpoint = 'md';

type MenuItem = Required<MenuProps>['items'][number];

const MainNavigation: FC = () => {
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const items: MenuItem[] = useMemo(() => {
    if (user?.role === undefined) return [];

    const siteMap = getSiteMap();

    return Object.values(siteMap)
      .filter((page) => page.icon !== undefined && (page.role === undefined || page.role.includes(user.role)))
      .map(({ title, icon, children, key }) => ({
        label: title,
        icon,
        key,
        children: children
          ?.filter((child) => child.role === undefined || child.role.includes(user.role))
          .filter((child) => child.emails === undefined || (user.email && child.emails.includes(user.email))),
        className: classes.menuItem,
      }));
  }, [user?.role, user?.email]);

  // pathname이 메뉴 key에 정확히 없으면 공통 prefix가 가장 긴 key를 선택
  const selectedKeys = useMemo(() => {
    const allKeys = items
      .flatMap((item) => {
        if (!item || !('key' in item)) return [];
        const itemKey = String(item.key);
        if ('children' in item && item.children) {
          return item.children.map((child) => (child && 'key' in child ? String(child.key) : '')).filter(Boolean);
        }
        return [itemKey];
      })
      .filter(Boolean);

    if (allKeys.includes(pathname)) return [pathname];

    const matched = allKeys
      .filter((key) => pathname.startsWith(key.replace(/\/[^/]+$/, '')))
      .sort((a, b) => b.length - a.length);

    return matched.length > 0 ? [matched[0]] : [pathname];
  }, [pathname, items]);

  const onClickMenu: MenuProps['onClick'] = useCallback(
    (e: { key: string }) => {
      navigate(e.key);
    },
    [navigate],
  );

  return (
    <Sider
      theme='light'
      breakpoint={breakpoint}
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      style={{ overflow: 'scroll' }}
    >
      <Link className={`${classes.logo} ${isProduction ? '' : classes.dev}`} to='/'>
        {collapsed ? <img alt='logo' src={logoWhite} /> : <span>Gooduncles</span>}
      </Link>
      <Menu mode='inline' selectedKeys={selectedKeys} items={items} onClick={onClickMenu}></Menu>
    </Sider>
  );
};

export default MainNavigation;
